import { useState, type HTMLAttributes, type Key, type ReactNode } from 'react';
import { Box, Button, Chip, Collapse, Stack, TableCell, TableRow, Typography } from '@mui/material';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { blendedRate, statusLabels } from '@zilian/contracts';
import { Tag, type Tone } from '@zilian/ui';

// Shared with the operations site; imported from here by the admin pages as before.
export {
  CountTabs,
  Empty,
  FitTableContainer,
  Heading,
  ListToolbar,
  Tag,
  usePagination,
  type CountTab,
  type StatusTone,
  type Tone,
} from '@zilian/ui';

/** Quantities are kept to 2 decimals and unit prices to 3 (厘) wherever people read or type them. */
export const QTY_DECIMALS = 2;
export const PRICE_DECIMALS = 3;

export const amount = (v: unknown) =>
  Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: QTY_DECIMALS });

/** A stored decimal as an editable string, e.g. "5000.000000" -> "5000", "0.02000000" -> "0.02". */
export const plain = (v: unknown, digits: number) =>
  v === null || v === undefined || v === '' ? '' : String(Number(Number(v).toFixed(digits)));

/** Keeps typed text a non-negative decimal with at most `digits` decimals. */
export const decimalInput = (text: string, digits: number, signed = false) => {
  // A signed field keeps one leading + or - (e.g. a stock increase or decrease).
  const sign = signed ? (text.trim().match(/^[+-]/)?.[0] ?? '') : '';
  const cleaned = text.replace(/[^\d.]/g, '');
  const [int, ...rest] = cleaned.split('.');
  return sign + (rest.length ? `${int}.${rest.join('').slice(0, digits)}` : int);
};

export const date = (v: unknown) => (v ? new Date(String(v)).toLocaleDateString('zh-CN') : '—');
const minute = (v: unknown) =>
  new Date(String(v)).toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Who took a shipped batch, in one line; null while no driver has confirmed it. */
export function pickupText(pickup: any) {
  if (!pickup) return null;
  const who = [pickup.name || '司机', pickup.phone, pickup.plate].filter(Boolean).join(' ');
  return `${who} · ${minute(pickup.at)} 扫码提货${pickup.note ? ` · ${pickup.note}` : ''}`;
}

/** Amounts always show 2 decimals so figures line up and read alike everywhere. */
export const money = (v: unknown) =>
  `¥ ${Number(v ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const unitPrice = (v: unknown) =>
  `¥ ${Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: PRICE_DECIMALS })}`;
/**
 * An accessory of an order line: part of the product, shipped with it as a whole (a cap with a
 * bottle). Lines carry them as agreed; the order dialog reads them from records and catalogs.
 */
export interface LineAccessory {
  skuId: string;
  name: string;
  spec?: string;
  unit: string;
  integer: boolean;
  perPiece: string | number;
}

/** How many of an accessory go with so many pieces; half a cap is still a cap, as on the server. */
export const accessoryCount = (pieces: unknown, a: LineAccessory) => {
  const n = (Number(pieces) || 0) * Number(a.perPiece);
  return a.integer ? Math.ceil(n - 1e-9) : Number(n.toFixed(QTY_DECIMALS));
};

/** A product's accessories in one line, for lists that name them in passing (shipping, print). */
export const accessoriesText = (list: LineAccessory[] | undefined, pieces: unknown) =>
  (list ?? []).map((a) => `${a.name} ×${amount(accessoryCount(pieces, a))}`).join('、');

const MONO = '"SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

/**
 * What goes out with a product line, under it: a toggle naming how many accessories there are,
 * and the list with each one's figure per piece and the total for the line's quantity. The same
 * on the order dialogs and the order pages.
 */
export function AccessoryPanel(p: {
  accessories: LineAccessory[];
  productUnit: string;
  pieces: unknown;
  defaultOpen?: boolean;
  /** Words before the total: what to prepare when ordering, what goes with it on an order. */
  totalLabel?: string;
}) {
  const [open, setOpen] = useState(!!p.defaultOpen);
  if (!p.accessories.length) return null;
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen(!open)}
        startIcon={<AccountTreeOutlined sx={{ fontSize: '16px !important' }} />}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ fontSize: 12.5, px: 1, '& .MuiButton-endIcon': { ml: 0.25 } }}
      >
        随附配套
        <Chip
          size="small"
          label={`${p.accessories.length} 项配件`}
          sx={{ mx: 1, height: 20, fontSize: 11, bgcolor: 'primary.light', color: 'primary.main' }}
        />
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          {open ? '收起配件清单' : '展开配件清单'}
        </Box>
      </Button>
      <Collapse in={open}>
        <Stack
          spacing={0.75}
          sx={{ mt: 1, ml: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'primary.light' }}
        >
          {p.accessories.map((a) => (
            <Box
              key={a.skuId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 1.5,
                py: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Chip
                  size="small"
                  label="配套"
                  variant="outlined"
                  color="primary"
                  sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                  {a.name}
                </Typography>
                <Typography color="text.disabled" sx={{ fontSize: 11 }}>
                  |
                </Typography>
                <Typography noWrap color="text.secondary" sx={{ fontSize: 12 }}>
                  {[a.spec, `每${p.productUnit || '个'}配 ${Number(a.perPiece)} ${a.unit}`]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  {p.totalLabel ?? '随单需备'}
                </Typography>
                <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 13 }}>
                  {Number(p.pieces) > 0 ? `${amount(accessoryCount(p.pieces, a))} ${a.unit}` : '—'}
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export { roleNames } from '@zilian/contracts';

const STATUS_TONES: Record<string, Tone> = {
  CLOSED: 'success',
  RECEIVED: 'success',
  PENDING: 'warning',
  OPEN: 'warning',
  REQUESTED: 'warning',
  CANCELLED: 'neutral',
  REJECTED: 'neutral',
  // Stock quality: usable stock is green, stock held for inspection needs attention.
  AVAILABLE: 'success',
  QUARANTINE: 'warning',
};

/**
 * The last column of a business list (orders, statements, partners to bill): the one next step
 * when it is this enterprise's turn, as a small filled button; otherwise a quiet 查看 › for rows
 * that open on a click. Master-data lists (items, counterparties) keep their text buttons.
 */
export function RowAction(p: { label?: string; onClick?: () => void; view?: boolean }) {
  if (p.label)
    return (
      <Button
        size="small"
        variant="contained"
        disableElevation
        sx={{ whiteSpace: 'nowrap' }}
        onClick={(e) => {
          // The row opens the detail; the button does its own step.
          e.stopPropagation();
          p.onClick?.();
        }}
      >
        {p.label}
      </Button>
    );
  if (p.view === false) return null;
  return (
    <Typography color="text.secondary" sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
      查看 ›
    </Typography>
  );
}

export function Status({ value }: { value: string }) {
  return <Tag tone={STATUS_TONES[value] ?? 'info'}>{statusLabels[value] ?? value}</Tag>;
}

/** Fuzzy item search: every typed word must appear in the name, spec or code, ignoring case. */
export const matchSkus = (options: any[], { inputValue }: { inputValue: string }) => {
  const words = inputValue.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return options;
  return options.filter((item) => {
    const text = `${item.name} ${item.spec ?? ''} ${item.code ?? ''}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });
};

/**
 * The one look for every item picker (Autocomplete): the name on top, spec · unit · code in
 * grey under it, fuzzy search, and a popper wider than a narrow field. `extra` adds to the grey
 * line, `aside` shows on the right (e.g. a price).
 */
export const skuPicker = (
  opts: { extra?: (item: any) => string; aside?: (item: any) => ReactNode } = {},
) => ({
  getOptionLabel: (item: any) => item.name ?? '',
  isOptionEqualToValue: (a: any, b: any) => a.id === b.id,
  filterOptions: matchSkus,
  noOptionsText: '没有匹配的商品，可换个关键词',
  slotProps: { popper: { placement: 'bottom-start' as const, sx: { minWidth: 420 } } },
  renderOption: (props: HTMLAttributes<HTMLLIElement> & { key?: Key }, item: any) => {
    const { key: _key, ...rest } = props;
    const aside = opts.aside?.(item);
    return (
      <li key={item.id} {...rest}>
        <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14 }}>{item.name}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              {[item.spec, item.unit, item.code].filter(Boolean).join(' · ')}
              {opts.extra?.(item) ?? ''}
            </Typography>
          </Box>
          {aside != null && (
            <Typography color="text.secondary" sx={{ fontSize: 13, flexShrink: 0 }}>
              {aside}
            </Typography>
          )}
        </Box>
      </li>
    );
  },
});

export interface Field {
  name: string;
  label: string;
  type?: string;
  /** With `multi`, `hint` is a one-line explanation under each option. */
  options?: { value: string; label: string; hint?: string }[];
  /** Pick any number of the options (checkboxes); the value is an array. */
  multi?: boolean;
  wide?: boolean;
  helper?: string;
  readOnly?: boolean;
  /** Numeric field: typed text is limited to this many decimals. */
  decimals?: number;
  /** Numeric field that also takes a leading + or -. */
  signed?: boolean;
  /** Common values offered on focus; free text is still allowed. */
  suggestions?: string[];
  /** Item picker over these items (searchable, two-line options); the value is the item id. */
  skus?: any[];
  /** Unit shown after the value, worked out from what is filled so far (e.g. the item picked). */
  unit?: (form: Record<string, any>) => string | undefined;
}
export type Props = Record<string, any>;

/**
 * Footer rows above an order's 合计 when its amount moves with raw material prices: goods at the
 * quoted prices, then the adjustment with the reason, so the total reads as a sum.
 */
export function AdjustRows(p: {
  goods: number;
  adjustment: number;
  reasons: string[];
  /** Columns before the amount column. */
  span: number;
  /** Columns after it. */
  trailing?: number;
}) {
  const label = { fontSize: 13, color: 'text.secondary', borderBottom: 'none', pb: 0.5 };
  const figure = { fontSize: 13, whiteSpace: 'nowrap', borderBottom: 'none', pb: 0.5 } as const;
  const rest = p.trailing ?? 1;
  return (
    <>
      <TableRow>
        <TableCell colSpan={p.span} sx={label}>
          货款（按报价）
        </TableCell>
        <TableCell align="right" sx={figure}>
          {money(p.goods)}
        </TableCell>
        {rest > 0 && <TableCell colSpan={rest} sx={{ borderBottom: 'none' }} />}
      </TableRow>
      <TableRow>
        <TableCell colSpan={p.span} sx={{ ...label, pt: 0.5, verticalAlign: 'top' }}>
          原料调整 {blendedRate(p.goods, p.adjustment)}
          {p.reasons.map((r) => (
            <Box key={r} sx={{ fontSize: 12, mt: 0.25 }}>
              {r}
            </Box>
          ))}
        </TableCell>
        <TableCell align="right" sx={{ ...figure, pt: 0.5, verticalAlign: 'top' }}>
          {p.adjustment > 0 ? '+' : '-'}
          {money(Math.abs(p.adjustment))}
        </TableCell>
        {rest > 0 && <TableCell colSpan={rest} sx={{ borderBottom: 'none' }} />}
      </TableRow>
    </>
  );
}
